//@ts-nocheck
import React, { useEffect, useState } from "react";
//@ts-ignore
import { tw } from "@careinfo/tailwind";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { classNames } from "../../../utils/classNames";
import { useNavigate, useSearchParams } from "react-router-dom";
import useGetHook from "../../../customHooks/useGetHook";
import CreateIcon from "../../../Icons/CreateIcon";
import Spinner from "../Spinner";
//@ts-ignore
import {
  PermissionRequired,
  AccessControl,
} from "@careinfo/permission-required";
import { TableSettingsLayout } from "@careinfo/layout";
import { useDebounce } from "../../../customHooks/useDebounceHook";
import Template from "./loading";

interface IDataTable {
  url: string;
  queryKey: string;
  columns: any;
  addNewPagePath?: string;
  tableName: string;
  params?: any;
  scanComponent?: any;
  actionMenu?: any;
  setRerender?: any;
  isUpload?: boolean;
  onRowClick?: any;
  isDownload?: boolean;
}

function DataTable(props: IDataTable) {
  const {
    url,
    queryKey,
    columns,
    addNewPagePath,
    scanComponent,
    actionMenu,
    onRowClick,
    tableName,
    params: otherParams,
    isUpload = false,
    isDownload = false,
  } = props;
  const [searchParams, setSearchParms] = useSearchParams({
    limit: "10",
    page: "0",
    searchQuery: "",
  });
  const [limit, setLimit] = useState(10);
  const page = parseInt(searchParams.get("page") ?? "0");
  const searchQuery = searchParams.get("searchQuery");
  const [isOpenTableSetting, setIsOpenTableSettings] = useState(false);
  const selectedItems = searchParams.get("selectedItems");
  const navigate = useNavigate();
  const debounceSearch = useDebounce(searchQuery);

  const [countSelectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    const items = selectedItems?.split(",");
    setSelectedItems(items?.length);
  }, [selectedItems]);

  const { data: serverData, isLoading } = useGetHook({
    queryKey: [
      queryKey,
      `${queryKey}-${limit}-${page}-${debounceSearch}-${JSON.stringify(
        otherParams
      )}`,
    ],
    url: url,
    params: {
      limit: limit,
      offset: limit * page,
      search: debounceSearch,
      ...otherParams,
    },
  });
  const [data, setData] = useState([]);

  useEffect(() => {
    if (Array.isArray(serverData?.results)) {
      setData([...serverData?.results] ?? []);
      if (searchParams.get("selectAllItems") === "1") {
        let _newItems = serverData?.results
          ?.map((item) => item.id)
          .join(",")
          .toString();
        setSearchParms({
          page: page,
          selectedItems: _newItems,
          selectAllItems: searchParams.get("selectAllItems"),
          searchQuery: searchQuery,
        });
      }
    }
  }, [serverData]);

  // Create the table and pass your options
  const table = useReactTable({
    data,
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handleAddPageRoute = () => {
    navigate(addNewPagePath);
  };

  useEffect(() => {
    table.setPageSize(limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  const handleNextPage = () => {
    searchParams.set("page", page + 1);
    setSearchParms(searchParams);
  };

  const handlePreviousPage = () => {
    searchParams.set("page", page - 1);
    setSearchParms(searchParams);
  };

  const handleLastpage = () => {
    searchParams.set("page", Math.floor(serverData?.count / limit));
    setSearchParms(searchParams);
  };

  const handleFirstPage = () => {
    searchParams.set("page", 0);
    setSearchParms(searchParams);
  };

  const handleSearch = (value) => {
    searchParams.set("searchQuery", value);
    searchParams.set("page", 0);
    setSearchParms(searchParams);
  };

  return isLoading ? (
    <Template addNewPath={addNewPagePath} />
  ) : (
    <>
      {addNewPagePath && (
        <>
          <div className={tw`w-full flex justify-between`}>
            <AccessControl
              children={
                <button
                  onClick={handleAddPageRoute}
                  className={tw`p-2 flex gap-x-2 bg-primary-light my-2 rounded text-primary focus:outline-none`}
                >
                  <CreateIcon color={"var(--primary)"} />
                  Add New
                </button>
              }
              allowedPermissions={["can_create_group"]}
            />
            {countSelectedItems ? (
              <>
                <span className={tw`text-primary`}>
                  Selected Items:{countSelectedItems ?? 0}
                </span>
              </>
            ) : (
              tw``
            )}
            {scanComponent}
          </div>
        </>
      )}
      <div
        className={tw`w-full bg-white rounded-sm mb-8`}
        style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)" }}
      >
        <div
          className={tw`w-full grid grid-cols-1 gap-4 md:flex justify-between p-2`}
        >
          <div>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery ?? ""}
              onChange={(e) => handleSearch(e?.target?.value)}
              className={tw`border w-full border-1 border-primary focus:outline-none p-1`}
            />
          </div>
          <div className={tw`text-primary w-full justify-end flex gap-x-4`}>
            <span className={tw`flex gap-x-1 w-full cursor-pointer`}>
              {actionMenu}
            </span>
          </div>
          <TableSettingsLayout
            isOpenTableSetting={isOpenTableSetting}
            setIsOpenTableSettings={setIsOpenTableSettings}
            tableName={tableName}
            defaultColumns={columns}
            onSettingsLoaded={(settings) => {
              if (settings?.page_limit != null) {
                setLimit(settings.page_limit);
                searchParams.set("limit", settings.page_limit.toString());
                setSearchParms(searchParams);
              }
            }}
          />
        </div>
        <div className={tw`w-full overflow-auto z-0`}>
          <table className={tw`w-full`}>
            <thead className={tw`bg-primary text-white uppercase`}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={tw``}>
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={classNames(
                        index === 0
                          ? tw``
                          : headerGroup.headers?.length - 1 === index
                          ? tw` text-left w-[8rem]`
                          : tw``,
                        tw`py-[5px] text-left px-5 whitespace-nowrap`
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={tw`tweven:bg-gray-50 odd:bg-white hover:bg-gray-100 hover:transition-all cursor-pointer`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={tw`py-[10px] text-left px-5`}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
        <div
          className={tw`flex bg-white py-2 my-4 w-full justify-center gap-4`}
        >
          <button
            className={tw`border hidden md:block rounded p-1`}
            onClick={() => handleFirstPage()}
            disabled={!serverData?.previous}
          >
            {"<<"}
          </button>
          <button
            className={tw`border rounded p-1`}
            onClick={() => handlePreviousPage()}
            disabled={!serverData?.previous}
          >
            {"<"}
          </button>
          <button
            className={tw`border rounded p-1`}
            onClick={() => handleNextPage()}
            disabled={!serverData?.next}
          >
            {">"}
          </button>
          <button
            className={tw`border hidden md:block rounded p-1`}
            onClick={() => handleLastpage()}
            disabled={!serverData?.next}
          >
            {">>"}
          </button>
          <span className={tw`flex items-center gap-1`}>
            <div>Page</div>
            <strong>
              {page + 1} of {Math.ceil(serverData?.count / limit)}
            </strong>
          </span>
          {/* <span className="flex items-center gap-1">
              | Go to page:
              <input
                type="number"
                min={1}
                value={page}
                max={Math.ceil(serverData?.count/limit)}
                onChange={e => {setPage(Number(e.target.value))}}
                className="border p-1 rounded w-16"
              />
            </span> */}
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              searchParams.set("limit", e?.target?.value);
              setSearchParms(searchParams);
            }}
          >
            {[10, 50, 100, 200, 500].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
          <p className={tw`mt-1`}>All: {serverData?.count}</p>
        </div>
      </div>
    </>
  );
}

export default DataTable;
